import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { TenantIngestionJob } from 'src/app/shared/models/tenant-ingestion-job';
import { TenantIngestionJobRequest } from '../../models/tenant-ingestion-job-request';
import { TenantIngestionJobResponse } from '../../models/tenant-ingestion-job-response';
import { TenantApiService } from '../http/tenant-api.service';
import AutoMapperService from './auto-mapper.service';
import { TenantIngestionJobV1 } from 'src/app/shared/models/tenant-ingestion-job-v1';

import { TenantIngestionIdentifiers } from 'src/app/shared/models/tenant-ingestion-identifiers';

@Injectable({
    providedIn: 'root'
})
export class TenantIngestionJobService {
    constructor(private tenantApiService: TenantApiService) {}

    getTenantIngestionJobsV2(tenantId: string, sourceId: number, entityId: number): Observable<TenantIngestionJob[]> {
        return this.tenantApiService.getTenantSourceEntityIngestionsV2(tenantId, sourceId, entityId).pipe(
            map(response => {
                const jobs: TenantIngestionJob[] =
                    response?.map(data =>
                        AutoMapperService.mapToApplicationModel<TenantIngestionJobResponse, TenantIngestionJob>(data)
                    ) ?? [];

                jobs.forEach(j =>
                    AutoMapperService.convertStringsToDates(j, 'rawStartedUtc', 'rawLastUtc', 'rawNextUtc')
                );

                return jobs;
            })
        );
    }

    getTenantIngestionJobsV1(tenantId: string, queryIds: number[], query: string): Observable<TenantIngestionJobV1[]> {
        return this.tenantApiService.getTenantIngestionsV1(tenantId, queryIds, query).pipe(
            map(response => {
                const jobs: TenantIngestionJobV1[] =
                    response?.map(data =>
                        AutoMapperService.mapToApplicationModel<TenantIngestionJobResponse, TenantIngestionJobV1>(data)
                    ) ?? [];

                jobs.forEach(j =>
                    AutoMapperService.convertStringsToDates(j, 'rawStartedUtc', 'rawLastUtc', 'rawNextUtc')
                );

                return jobs;
            })
        );
    }

    saveTenantIngestionJobs(tenantIngestionJob: TenantIngestionJob) {
        const tenantIngestionJobRequest = this.mapToRequest(tenantIngestionJob);

        return this.tenantApiService.saveTenantIngestionJob(tenantIngestionJobRequest);
    }

    updateTenantIngestionJobs(tenantIngestionJob: TenantIngestionJob) {
        const tenantIngestionJobRequest = this.mapToRequest(tenantIngestionJob);

        return this.tenantApiService.updateTenantIngestionJob(tenantIngestionJobRequest);
    }

    queueTenantJobs(tenantId: string, jobIds: number[]): Observable<object> {
        return this.tenantApiService.bulkQueueTenantScheduleJobs(tenantId, jobIds);
    }

    activateTenantJobs(tenantId: string, jobIds: number[]): Observable<object> {
        return this.tenantApiService.activateTenantJobs(tenantId, jobIds);
    }

    deactivateTenantJobs(tenantId: string, jobIds: number[]): Observable<object> {
        return this.tenantApiService.deactivateTenantJobs(tenantId, jobIds);
    }

    deleteSingleTenantIngestionJob(ingestion: TenantIngestionIdentifiers): Observable<object> {
        return this.tenantApiService.deleteSingleTenantIngestionJob(ingestion);
    }

    deleteMultipleTenantIngestionJobs(ingestions: TenantIngestionIdentifiers): Observable<object> {
        return this.tenantApiService.deleteMultipleTenantIngestionJobs(ingestions);
    }

    private mapToRequest(tenantIngestionJob: TenantIngestionJob) {
        return AutoMapperService.mapToApiModel<TenantIngestionJob, TenantIngestionJobRequest>(tenantIngestionJob);
    }
}
